import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Input, Button, Avatar, Spin, Tooltip, message as antdMessage } from "antd";
import {
  SendOutlined,
  PictureOutlined,
  SearchOutlined,
  UserOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  fetchChatConversations,
  fetchConversationMessages,
  sendChatMessage,
  markConversationRead,
  uploadMedia,
} from "../api/auth";
import type { ChatConversation, ChatMessage } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useChatSocket } from "../hooks/useChatSocket";
import { PageShell } from "../components/Page";

function formatTime(dateStr: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: "short" });
  } else {
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }
}

export default function MessagesPage() {
  const { seller } = useAuth();
  const qc = useQueryClient();
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [inputText, setInputText] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const typingTimeoutRef = useRef<any>(null);

  const {
    connected,
    socket,
    joinConversation,
    leaveConversation,
    sendMessage: socketSendMessage,
    markRead: socketMarkRead,
    startTyping,
    stopTyping,
    isOnline,
  } = useChatSocket();

  // 1. Fetch conversations
  const {
    data: convResponse,
    isLoading: convsLoading,
    refetch: refetchConversations,
  } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => fetchChatConversations(1, 50),
    refetchInterval: 30000,
  });

  const rawConversations: ChatConversation[] = useMemo(() => {
    if (!convResponse) return [];
    if (Array.isArray(convResponse)) return convResponse;
    if (Array.isArray((convResponse as any).data)) return (convResponse as any).data;
    if (Array.isArray((convResponse as any).rows)) return (convResponse as any).rows;
    return [];
  }, [convResponse]);

  // Helper to safely update chat-conversations cache regardless of array/envelope shape
  const updateCacheConversations = useCallback(
    (updater: (list: ChatConversation[]) => ChatConversation[]) => {
      qc.setQueryData(["chat-conversations"], (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return updater(old);
        }
        if (Array.isArray(old.data)) {
          return { ...old, data: updater(old.data) };
        }
        return old;
      });
    },
    [qc],
  );

  // Filter conversations by search
  const filteredConversations = useMemo(() => {
    if (!search.trim()) return rawConversations;
    const q = search.toLowerCase();
    return rawConversations.filter((c) => {
      const otherPart =
        c.participants?.find((p) => p.accountId !== seller?.accountId) ||
        c.participants?.find((p) => p.accountId === c.buyerAccountId);
      const buyerName = otherPart?.account?.displayName || "";
      const bikeTitle = c.vehicle?.title || c.listing?.title || "";
      return (
        buyerName.toLowerCase().includes(q) ||
        bikeTitle.toLowerCase().includes(q) ||
        (c.lastMessage?.content || c.lastMessage?.body || "").toLowerCase().includes(q)
      );
    });
  }, [rawConversations, search, seller?.accountId]);

  // Set default selected conversation if none selected
  useEffect(() => {
    if (!selectedConvId && filteredConversations.length > 0) {
      setSelectedConvId(filteredConversations[0].id);
    }
  }, [filteredConversations, selectedConvId]);

  const activeConversation = useMemo(
    () => rawConversations.find((c) => c.id === selectedConvId) || null,
    [rawConversations, selectedConvId],
  );

  // 2. Fetch messages for active conversation
  const {
    data: msgResponse,
    isLoading: msgsLoading,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: ["chat-messages", selectedConvId],
    queryFn: () => (selectedConvId ? fetchConversationMessages(selectedConvId, undefined, 50) : null),
    enabled: !!selectedConvId,
  });

  useEffect(() => {
    if (!msgResponse) return;
    const rawList: ChatMessage[] = Array.isArray(msgResponse)
      ? msgResponse
      : Array.isArray((msgResponse as any).data)
        ? (msgResponse as any).data
        : [];
    // Sort chronologically ascending (oldest at top, newest at bottom)
    const sorted = [...rawList].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    setMessages(sorted);
  }, [msgResponse]);

  // 3. Socket event listener for active room
  useEffect(() => {
    if (!selectedConvId) return;

    joinConversation(selectedConvId);
    markConversationRead(selectedConvId).catch(() => {});
    socketMarkRead(selectedConvId);
    qc.invalidateQueries({ queryKey: ["chat-unread-count"] });

    // Decrement unread locally in react-query cache
    updateCacheConversations((list: ChatConversation[]) =>
      list.map((c: ChatConversation) => (c.id === selectedConvId ? { ...c, unreadCount: 0 } : c)),
    );

    return () => {
      leaveConversation(selectedConvId);
    };
  }, [selectedConvId, joinConversation, leaveConversation, socketMarkRead, updateCacheConversations, qc]);

  // Listen for real-time messages & typing from socket
  useEffect(() => {
    if (!socket) return;

    const onNewMessage = (msg: ChatMessage) => {
      // If message is for currently open conversation, append to stream
      if (msg.conversationId === selectedConvId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        markConversationRead(selectedConvId).catch(() => {});
        socketMarkRead(selectedConvId);
        qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
      }

      // Update last message in conversation list
      updateCacheConversations((list: ChatConversation[]) => {
        const exists = list.some((c: ChatConversation) => c.id === msg.conversationId);
        if (!exists) {
          qc.invalidateQueries({ queryKey: ["chat-conversations"] });
          return list;
        }
        return list.map((c: ChatConversation) => {
          if (c.id === msg.conversationId) {
            return {
              ...c,
              lastMessageAt: msg.createdAt,
              lastMessage: {
                id: msg.id,
                content: msg.content || msg.body || "",
                body: msg.body || msg.content || "",
                messageType: msg.messageType,
                senderId: msg.senderId || msg.senderAccountId || "",
                createdAt: msg.createdAt,
              },
              unreadCount:
                c.id === selectedConvId
                  ? 0
                  : (c.unreadCount || 0) + (msg.senderAccountId === seller?.accountId ? 0 : 1),
            };
          }
          return c;
        });
      });
    };

    const onTyping = (data: { conversationId: string; accountId: string; typing: boolean }) => {
      if (data.conversationId === selectedConvId && data.accountId !== seller?.accountId) {
        setIsTyping(data.typing);
      }
    };

    socket.on("message:new", onNewMessage);
    socket.on("typing", onTyping);

    return () => {
      socket.off("message:new", onNewMessage);
      socket.off("typing", onTyping);
    };
  }, [socket, selectedConvId, seller?.accountId, socketMarkRead, updateCacheConversations, qc]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Handle typing debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    if (!selectedConvId) return;

    startTyping(selectedConvId);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(selectedConvId);
    }, 2000);
  };

  const handleSendMessage = async (mediaUrl?: string) => {
    const text = inputText.trim();
    if (!text && !mediaUrl) return;
    if (!selectedConvId) return;

    setInputText("");
    stopTyping(selectedConvId);

    // Optimistic message
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg: ChatMessage = {
      id: tempId,
      conversationId: selectedConvId,
      senderId: seller?.accountId || "me",
      senderAccountId: seller?.accountId || "me",
      content: text || (mediaUrl ? "[Photo]" : ""),
      body: text || (mediaUrl ? "[Photo]" : ""),
      messageType: mediaUrl ? "IMAGE" : "TEXT",
      mediaUrl: mediaUrl || null,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMsg]);

    try {
      // Send via socket first for instant delivery, with REST fallback
      if (connected) {
        socketSendMessage({
          conversationId: selectedConvId,
          content: text || undefined,
          mediaUrl: mediaUrl || undefined,
        });
      } else {
        const saved = await sendChatMessage(selectedConvId, text, mediaUrl);
        setMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)));
      }
    } catch {
      antdMessage.error("Failed to send message. Please retry.");
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      antdMessage.error("Please upload an image file (JPEG, PNG, WebP)");
      return;
    }

    setUploadingImage(true);
    try {
      const asset = await uploadMedia(file);
      await handleSendMessage(asset.url);
      antdMessage.success("Photo sent");
    } catch {
      antdMessage.error("Failed to upload photo");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Helper to extract counterparty info
  const otherAccountId =
    activeConversation?.buyerAccountId === seller?.accountId
      ? activeConversation?.sellerId
      : activeConversation?.buyerAccountId;

  const buyerParticipant =
    activeConversation?.participants?.find((p) => p.accountId === otherAccountId) ||
    activeConversation?.participants?.find((p) => p.accountId !== seller?.accountId) ||
    activeConversation?.participants?.[0];

  const buyerName = buyerParticipant?.account?.displayName || "Customer";
  const buyerPhone = buyerParticipant?.account?.phone || null;
  const buyerOnline = otherAccountId ? isOnline(otherAccountId) : false;

  return (
    <PageShell className="h-[calc(100vh-32px)] flex flex-col p-4 md:p-6 overflow-hidden">
      <div className="flex items-center justify-between pb-3 border-b border-border mb-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-ink">Messages & Inquiries</h1>
          <p className="text-xs text-ink-muted">
            Live real-time messaging with buyers inquiring about your bikes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              connected
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-amber-50 text-amber-700 border border-amber-200"
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                connected ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
              }`}
            />
            {connected ? "Live Socket Connected" : "Connecting..."}
          </span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              refetchConversations();
              if (selectedConvId) refetchMessages();
            }}
          />
        </div>
      </div>

      <div className="flex flex-1 min-h-0 bg-white border border-border rounded-xl shadow-sm overflow-hidden">
        {/* Left column: Conversations list */}
        <div className="w-80 md:w-96 flex flex-col border-r border-border bg-slate-50/50">
          <div className="p-3 border-b border-border bg-white">
            <Input
              placeholder="Search conversations or bikes..."
              prefix={<SearchOutlined className="text-slate-400" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className="rounded-lg"
            />
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-border/60">
            {convsLoading ? (
              <div className="p-8 text-center">
                <Spin size="small" />
                <p className="mt-2 text-xs text-ink-muted">Loading conversations...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-ink-muted text-xs">
                No conversations yet. When buyers message you from BikerHat Web, inquiries appear here.
              </div>
            ) : (
              filteredConversations.map((c) => {
                const otherPart =
                  c.participants?.find((p) => p.accountId !== seller?.accountId) ||
                  c.participants?.find((p) => p.accountId === c.buyerAccountId) ||
                  c.participants?.[0];
                const name = otherPart?.account?.displayName || "Customer";
                const isSelected = c.id === selectedConvId;
                const counterId = otherPart?.accountId || c.buyerAccountId;
                const online = counterId ? isOnline(counterId) : false;
                const bike = c.vehicle || c.listing;

                return (
                  <div
                    key={c.id}
                    onClick={() => setSelectedConvId(c.id)}
                    className={`p-3 cursor-pointer transition-colors relative flex items-start gap-3 ${
                      isSelected
                        ? "bg-slate-100/90 border-l-4 border-l-primary"
                        : "hover:bg-white/80"
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <Avatar
                        size={42}
                        className="bg-primary/10 text-primary font-semibold uppercase"
                      >
                        {name[0] || <UserOutlined />}
                      </Avatar>
                      {online && (
                        <span
                          className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white"
                          title="Online now"
                        />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-semibold text-sm text-ink truncate max-w-[140px]">
                          {name}
                        </span>
                        <span className="text-[11px] text-ink-muted flex-shrink-0">
                          {formatTime(c.lastMessageAt || c.createdAt)}
                        </span>
                      </div>

                      {bike && (
                        <div className="text-[11px] text-primary/80 font-medium truncate mb-1">
                          🏍️ {bike.title}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-ink-muted truncate max-w-[180px]">
                          {c.lastMessage?.content || c.lastMessage?.body || "Started a conversation"}
                        </span>
                        {!!c.unreadCount && c.unreadCount > 0 && (
                          <span className="h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] font-bold rounded-full bg-red-500 text-white shadow-sm">
                            {c.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right column: Active conversation thread */}
        {activeConversation ? (
          <div className="flex-1 flex flex-col bg-white">
            {/* Header */}
            <div className="p-3.5 border-b border-border flex items-center justify-between bg-slate-50/40">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar size={44} className="bg-primary text-white font-bold">
                    {buyerName[0] || <UserOutlined />}
                  </Avatar>
                  {buyerOnline && (
                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 border-2 border-white" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-base text-ink">{buyerName}</span>
                    {buyerOnline ? (
                      <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        Online
                      </span>
                    ) : (
                      <span className="text-[11px] text-ink-muted">Offline</span>
                    )}
                  </div>
                  {buyerPhone && (
                    <div className="text-xs text-ink-muted font-mono">{buyerPhone}</div>
                  )}
                </div>
              </div>

              {/* Bike Listing Badge */}
              {(activeConversation.vehicle || activeConversation.listing) && (
                <div className="hidden sm:flex items-center gap-2.5 p-2 bg-white rounded-lg border border-border shadow-xs max-w-sm">
                  {activeConversation.vehicle?.thumbnailUrl ? (
                    <img
                      src={
                        activeConversation.vehicle.thumbnailUrl.startsWith("http")
                          ? activeConversation.vehicle.thumbnailUrl
                          : `https://api.bikerhat.com${activeConversation.vehicle.thumbnailUrl}`
                      }
                      alt="Bike"
                      className="h-10 w-14 object-cover rounded border border-border flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-12 bg-slate-100 rounded flex items-center justify-center text-base">
                      🏍️
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-ink truncate">
                      {activeConversation.vehicle?.title || activeConversation.listing?.title}
                    </div>
                    <div className="text-xs text-primary font-bold">
                      ৳
                      {(
                        activeConversation.vehicle?.price || activeConversation.listing?.price || 0
                      ).toLocaleString("en-BD")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Stream */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f8fafc]">
              {msgsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 text-ink-muted">
                  <div className="text-3xl mb-2">💬</div>
                  <p className="text-sm font-medium">No messages in this inquiry yet.</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Send a message to greet the buyer and provide bike details.
                  </p>
                </div>
              ) : (
                messages.map((m) => {
                  const isMine = m.senderAccountId === seller?.accountId || m.senderId === seller?.accountId;

                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-xs text-sm ${
                          isMine
                            ? "bg-slate-900 text-white rounded-br-xs"
                            : "bg-white text-ink border border-border rounded-bl-xs"
                        }`}
                      >
                        {m.mediaUrl && (
                          <div className="mb-2">
                            <img
                              src={
                                m.mediaUrl.startsWith("http")
                                  ? m.mediaUrl
                                  : `https://api.bikerhat.com${m.mediaUrl}`
                              }
                              alt="Attachment"
                              className="rounded-lg max-h-60 object-cover cursor-pointer hover:opacity-95"
                              onClick={() =>
                                window.open(
                                  m.mediaUrl?.startsWith("http")
                                    ? m.mediaUrl
                                    : `https://api.bikerhat.com${m.mediaUrl}`,
                                  "_blank",
                                )
                              }
                            />
                          </div>
                        )}
                        {m.content || m.body ? (
                          <p className="whitespace-pre-wrap break-words leading-relaxed">
                            {m.content || m.body}
                          </p>
                        ) : null}
                      </div>
                      <span className="text-[10px] text-ink-muted mt-1 px-1">
                        {formatTime(m.createdAt)}
                      </span>
                    </div>
                  );
                })
              )}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex items-center gap-1.5 text-xs text-ink-muted italic py-1 px-2">
                  <span className="inline-flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" />
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0.2s]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0.4s]" />
                  </span>
                  <span>{buyerName} is typing...</span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Composer */}
            <div className="p-3 bg-white border-t border-border">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileUpload}
                />
                <Tooltip title="Send Photo">
                  <Button
                    type="text"
                    icon={<PictureOutlined />}
                    loading={uploadingImage}
                    onClick={() => fileInputRef.current?.click()}
                    className="text-slate-500 hover:text-primary"
                  />
                </Tooltip>

                <Input
                  value={inputText}
                  onChange={handleInputChange}
                  placeholder="Type a message to the buyer... (Press Enter to send)"
                  className="rounded-full px-4 py-2"
                  autoFocus
                />

                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  disabled={!inputText.trim()}
                  className="rounded-full px-5 bg-slate-900 hover:bg-slate-800"
                >
                  Send
                </Button>
              </form>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-ink-muted bg-slate-50/30">
            <div className="text-4xl mb-3">💬</div>
            <h3 className="font-semibold text-ink text-base">Select a conversation</h3>
            <p className="text-xs max-w-sm mt-1">
              Choose an inquiry from the left panel to review questions from buyers and reply in real time.
            </p>
          </div>
        )}
      </div>
    </PageShell>
  );
}
